import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import OriginTracer from '@atlassiansox/origin-tracing';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { ufoEventOnboardingInviteUsers } from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { EmailInviteRequest } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

import { useCloudStatus } from '../../../../../hooks/use-cloud-status';
import { generateContinueUrl, getEmailListAnalytics } from '../utils';

import { isValidInviteEmail } from './use-email-invites-input';
import { useResolveInviteTarget } from './use-resolve-invite-target';
import { getInviteeProjectRoleId } from './utils';

import type { UseJiraOnboardingInvitesHandler } from './use-jira-onboarding-invites-handler.types';

const JIRA_FREE_EDITION = 'free';
const MAX_ALLOWED_EMAILS = 10;

const sanitizeInviteEmails = (emails: string[]): string[] => {
	const seen = new Set<string>();
	const sanitizedEmails: string[] = [];

	for (const email of emails) {
		const candidate = email.trim();
		const key = candidate.toLowerCase();

		if (isValidInviteEmail(candidate) && !seen.has(key)) {
			seen.add(key);
			sanitizedEmails.push(candidate);
		}
	}

	return sanitizedEmails;
};

/**
 * Sends onboarding-wizard email invites for a freshly created Jira project.
 *
 * Resolves the invite target (project id, roles, edition) via {@link useResolveInviteTarget},
 * then posts one invite per email in parallel. All failures are captured in analytics/UFO — no
 * error is surfaced to the user. No-ops when there is no `taskId` or no emails.
 *
 * Do NOT use outside of fg: spice-refactor-jira-onboarding-wizard-invites, contact #help-spice for details
 */
export const useJiraOnboardingInvitesHandler = (): UseJiraOnboardingInvitesHandler => {
	const stargateClient = useBxpWebStargateClient();
	const { cloudId } = useCloudStatus();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { taskIdParam } = useSearchParameters();
	const { resolveProjectInformation } = useResolveInviteTarget();

	const invite = async (emails: string[]): Promise<void> => {
		const sanitizedEmails = sanitizeInviteEmails(emails);

		if (
			!taskIdParam ||
			sanitizedEmails.length === 0 ||
			sanitizedEmails.length > MAX_ALLOWED_EMAILS
		) {
			return;
		}

		ufoEventOnboardingInviteUsers.start();

		const target = await resolveProjectInformation(taskIdParam);
		if (!target) {
			// analytics/UFO already emitted by the resolver
			return;
		}

		const { projectId, projectRoleIds, jiraEdition } = target;
		const inviteAsAdmin = jiraEdition === JIRA_FREE_EDITION || jiraEdition === null;
		const inviteeRoleId = getInviteeProjectRoleId(projectRoleIds, inviteAsAdmin);

		const continueUrl = generateContinueUrl(projectId);
		const atlOrigin = new OriginTracer({ product: 'jira' }).encode();

		ufoEventOnboardingInviteUsers.startSubstep(
			UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_INVITING_USERS,
		);

		const invites = sanitizedEmails.map((email) => {
			const request: EmailInviteRequest = {
				email,
				projectRoleIds: inviteeRoleId ? [inviteeRoleId] : [],
				continueUrl,
				atlOrigin,
			};

			return stargateClient.postEmailInvite(cloudId, projectId, request).catch((error: unknown) => {
				createAnalyticsEvent({
					action: 'failed',
					actionSubject: 'onboardingWizardInviteStepInviteRequest',
					eventType: 'operational',
					attributes: {
						error: error instanceof Error ? error.message : String(error),
						projectId,
						inviteeRoleId,
						jiraEdition,
					},
				}).fire();
				throw error;
			});
		});

		const results = await Promise.allSettled(invites);

		ufoEventOnboardingInviteUsers.endSubstep(
			UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_INVITING_USERS,
		);

		const successfulInvites = results.filter((result) => result.status === 'fulfilled').length;
		const failedInvites = results.length - successfulInvites;
		const { emailCount, isCrossDomain } = getEmailListAnalytics(sanitizedEmails);

		createAnalyticsEvent({
			action: 'succeeded',
			actionSubject: 'bulkEmailInvite',
			eventType: 'track',
			attributes: {
				totalInvites: results.length,
				successfulInvites,
				failedInvites,
				emailCount,
				isCrossDomain,
				projectId,
				inviteeRoleId,
				jiraEdition,
			},
		}).fire();
	};

	return { invite };
};
